import { otherColors } from '@ehrTheme/colors';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AccordionCard } from 'src/components/AccordionCard';
import { RoundedButton } from 'src/components/RoundedButton';
import { dataTestIds } from 'src/constants/data-test-ids';
import AiSuggestion from 'src/features/visits/in-person/components/AiSuggestion';
import { TRACKING_BOARD_QUERY_KEY } from 'src/hooks/useGetTrackingBoard';
import { formatProcedureCptCode } from 'utils/lib/procedure-coding/format';
import { AiObservationField } from 'utils/lib/types/api/chart-data/chart-data.constants';
import { ObservationTextFieldDTO } from 'utils/lib/types/data/screening-questions/types';
import { Loader } from '../../shared/components/Loader';
import { PageTitle } from '../../shared/components/PageTitle';
import { useChartData } from '../../shared/hooks/useChartData';
import { useGetAppointmentAccessibility } from '../../shared/hooks/useGetAppointmentAccessibility';
import { useDeleteChartData } from '../../shared/stores/appointment/appointment.store';
import { useDeleteProcedureDialog } from '../components/DeleteProcedureDialog';
import { ROUTER_PATH } from '../routing/routesInPerson';

interface ProceduresBodyProps {
  onNewProcedure: () => void;
  onProcedureClick: (procedureId: string | undefined) => void;
  // Rendered to the left of the "Procedure" button; the page passes its PageTitle here,
  // the inline flow leaves it empty so the button aligns to the right on its own.
  pageTitle?: ReactNode;
}

export function ProceduresBody({ onNewProcedure, onProcedureClick, pageTitle }: ProceduresBodyProps): ReactElement {
  const { isChartDataLoading, chartData, refetch: refetchChartData } = useChartData();
  const appointmentAccessibility = useGetAppointmentAccessibility();
  const { mutateAsync: deleteChartData } = useDeleteChartData();
  const queryClient = useQueryClient();
  const aiProcedures = chartData?.observations?.filter(
    (observation) => observation.field === AiObservationField.Procedures
  ) as ObservationTextFieldDTO[];

  const isReadOnly = useMemo(() => {
    return appointmentAccessibility.isAppointmentReadOnly;
  }, [appointmentAccessibility.isAppointmentReadOnly]);

  const handleDeleteProcedure = useCallback(
    async ({ procedureId }: { procedureId: string; procedureName: string }): Promise<boolean> => {
      const procedureToDelete = chartData?.procedures?.find((p) => p.resourceId === procedureId);
      if (!procedureToDelete) {
        console.error('Procedure not found');
        return false;
      }

      try {
        await deleteChartData(
          {
            procedures: [procedureToDelete],
            cleanupProcedureDiagnoses: true,
          },
          {
            onSuccess: async () => {
              await refetchChartData();

              void queryClient.invalidateQueries({
                queryKey: [TRACKING_BOARD_QUERY_KEY],
                refetchType: 'active',
              });

              enqueueSnackbar('Procedure deleted successfully', { variant: 'success' });
            },
            onError: () => {
              enqueueSnackbar('Failed to delete procedure. Please try again.', { variant: 'error' });
            },
          }
        );
        return true;
      } catch {
        enqueueSnackbar('Failed to delete procedure. Please try again.', { variant: 'error' });
        return false;
      }
    },
    [chartData?.procedures, deleteChartData, refetchChartData, queryClient]
  );

  const { showDeleteProcedureDialog, DeleteProcedureDialog } = useDeleteProcedureDialog({
    deleteProcedure: handleDeleteProcedure,
  });

  const activeProcedures = useMemo(() => chartData?.procedures ?? [], [chartData?.procedures]);

  if (isChartDataLoading) return <Loader />;
  return (
    <Box>
      <Box display="flex" justifyContent={pageTitle != null ? 'space-between' : 'flex-end'} alignItems="center" mb={2}>
        {pageTitle}
        <RoundedButton variant="contained" onClick={onNewProcedure} startIcon={<AddIcon />} disabled={isReadOnly}>
          Procedure
        </RoundedButton>
      </Box>
      {aiProcedures?.length > 0 && (
        <Paper sx={{ padding: 2, marginBottom: 2 }}>
          <AiSuggestion title={'Procedures'} chartData={chartData} content={aiProcedures} />
        </Paper>
      )}
      <AccordionCard>
        <Table sx={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '50%' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: '500' }}>Procedure</Typography>
              </TableCell>
              <TableCell sx={{ width: '25%' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: '500' }}>Dx</Typography>
              </TableCell>
              <TableCell sx={{ width: '20%' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: '500' }}>Documented by</Typography>
              </TableCell>
              <TableCell sx={{ width: '5%' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {activeProcedures.map((procedure) => {
              const documentedDateTime =
                procedure.documentedDateTime != null ? DateTime.fromISO(procedure.documentedDateTime) : undefined;
              return (
                <TableRow
                  sx={{ '&:last-child td': { borderBottom: 0 }, cursor: 'pointer' }}
                  onClick={() => onProcedureClick(procedure.resourceId)}
                  key={procedure.resourceId}
                  data-testid={dataTestIds.proceduresPage.procedureRow}
                >
                  <TableCell>
                    <Stack>
                      {procedure.cptCodes?.map((cptCode, index) => {
                        return (
                          <Typography
                            sx={{ fontSize: '14px' }}
                            key={cptCode.resourceId ?? index}
                            data-testid={dataTestIds.proceduresPage.cptCode}
                          >
                            {formatProcedureCptCode(cptCode)}
                          </Typography>
                        );
                      })}
                      <Typography
                        sx={{ fontSize: '14px', color: '#00000099' }}
                        data-testid={dataTestIds.proceduresPage.procedureType}
                      >
                        {procedure.procedureType}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      {procedure.diagnoses?.map((diagnosis) => {
                        return (
                          <Typography
                            sx={{ fontSize: '14px' }}
                            key={diagnosis.code}
                            data-testid={dataTestIds.proceduresPage.diagnosis}
                          >
                            {diagnosis.code}-{diagnosis.display}
                          </Typography>
                        );
                      })}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography sx={{ fontSize: '14px' }}>
                        {documentedDateTime != null ? documentedDateTime.toFormat('MM/dd/yyyy hh:mm a') : undefined}
                      </Typography>
                      <Typography
                        sx={{ fontSize: '14px', color: '#00000099' }}
                        data-testid={dataTestIds.proceduresPage.documentedBy}
                      >
                        {procedure.documentedBy}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {!isReadOnly && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!procedure.resourceId) return;
                          const procedureName =
                            procedure.cptCodes?.map(formatProcedureCptCode).join(', ') || procedure.procedureType || '';
                          showDeleteProcedureDialog({
                            procedureId: procedure.resourceId,
                            procedureName,
                          });
                        }}
                        sx={{
                          color: otherColors.priorityHighText,
                          '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {activeProcedures.length === 0 ? (
              <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell colSpan={4} align="center">
                  <Typography sx={{ fontSize: '14px', color: '#00000099' }}>No procedures</Typography>
                </TableCell>
              </TableRow>
            ) : undefined}
          </TableBody>
        </Table>
      </AccordionCard>
      {DeleteProcedureDialog}
    </Box>
  );
}

export default function Procedures(): ReactElement {
  const navigate = useNavigate();
  const { id: appointmentId } = useParams();

  const onNewProcedureClick = (): void => {
    navigate(`/in-person/${appointmentId}/${ROUTER_PATH.PROCEDURES_NEW}`);
  };
  const onProcedureClick = (procedureId: string | undefined): void => {
    navigate(`/in-person/${appointmentId}/procedures/${procedureId}`);
  };

  return (
    <ProceduresBody
      onNewProcedure={onNewProcedureClick}
      onProcedureClick={onProcedureClick}
      pageTitle={
        <PageTitle label="Procedures" showIntakeNotesButton={false} dataTestId={dataTestIds.proceduresPage.title} />
      }
    />
  );
}
